import React, { useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import { StarterKit } from '@tiptap/starter-kit';
import { Underline } from '@tiptap/extension-underline';
import { Color } from '@tiptap/extension-color';
import { TextStyle } from '@tiptap/extension-text-style';
import { Highlight } from '@tiptap/extension-highlight';
import { Subscript } from '@tiptap/extension-subscript';
import { Superscript } from '@tiptap/extension-superscript';
import { TextAlign } from '@tiptap/extension-text-align';
import { Link } from '@tiptap/extension-link';
import { HexColorPicker } from 'react-colorful';
import { Popover } from 'antd';
import styles from './RichTextEditor.module.scss';

const Divider = () => <div className={styles.divider} />;

const Btn = ({ onClick, active, title, children, style }) => (
  <button
    type="button"
    className={`${styles.toolbarBtn} ${active ? styles.active : ''}`}
    onClick={onClick}
    title={title}
    style={style}
  >
    {children}
  </button>
);

const ColorBtn = ({ color, label, isHighlight, active, title, onColorChange }) => (
  <Popover
    trigger="click"
    content={
      <div onMouseDown={(e) => e.preventDefault()}>
        <HexColorPicker color={color} onChange={onColorChange} />
        <p className={styles.colorHex}>{color}</p>
      </div>
    }
  >
    <button
      type="button"
      className={`${styles.toolbarBtn} ${active ? styles.active : ''}`}
      title={title}
    >
      <span className={styles.colorBtnInner}>
        {isHighlight ? (
          <span style={{ background: color, padding: '0 2px', borderRadius: 2, fontWeight: 700, lineHeight: 1.2 }}>
            {label}
          </span>
        ) : (
          <span style={{ color, fontWeight: 700 }}>{label}</span>
        )}
        <span className={styles.colorSwatch} style={{ background: color }} />
      </span>
    </button>
  </Popover>
);

const RichTextEditor = ({ value, onChange }) => {
  const [textColor, setTextColor] = useState('#000000');
  const [highlightColor, setHighlightColor] = useState('#ffff00');

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextStyle,
      Color,
      Highlight.configure({ multicolor: true }),
      Subscript,
      Superscript,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Link.configure({ openOnClick: false, HTMLAttributes: { target: '_blank', rel: 'noopener noreferrer' } }),
    ],
    content: value || '',
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      onChange(html === '<p></p>' ? '' : html);
    },
    editorProps: {
      attributes: { class: 'tiptap' },
    },
  });

  if (!editor) return null;

  const currentTextColor = editor.getAttributes('textStyle').color || textColor;
  const currentHighlight = editor.getAttributes('highlight').color || highlightColor;

  const getHeadingValue = () => {
    for (const level of [1, 2, 3]) {
      if (editor.isActive('heading', { level })) return `h${level}`;
    }
    return 'p';
  };

  const applyHeading = (val) => {
    if (val === 'p') editor.chain().focus().setParagraph().run();
    else editor.chain().focus().setHeading({ level: parseInt(val[1]) }).run();
  };

  const handleTextColor = (color) => {
    setTextColor(color);
    editor.chain().focus().setColor(color).run();
  };

  const handleHighlight = (color) => {
    setHighlightColor(color);
    editor.chain().focus().setHighlight({ color }).run();
  };

  const handleLink = () => {
    const existing = editor.getAttributes('link').href;
    if (editor.isActive('link')) {
      editor.chain().focus().unsetLink().run();
      return;
    }
    const url = window.prompt('URL:', existing || 'https://');
    if (!url) return;
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  return (
    <div className={styles.editorWrapper}>
      <div className={styles.toolbar}>

        {/* Headings */}
        <select
          className={styles.headingSelect}
          value={getHeadingValue()}
          onChange={(e) => applyHeading(e.target.value)}
        >
          <option value="p">Paragraph</option>
          <option value="h1">Heading 1</option>
          <option value="h2">Heading 2</option>
          <option value="h3">Heading 3</option>
        </select>

        <Divider />

        {/* Inline formatting */}
        <Btn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} title="Bold"><b>B</b></Btn>
        <Btn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} title="Italic" style={{ fontStyle: 'italic' }}>I</Btn>
        <Btn onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive('underline')} title="Underline" style={{ textDecoration: 'underline' }}>U</Btn>
        <Btn onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive('strike')} title="Strikethrough" style={{ textDecoration: 'line-through' }}>S</Btn>

        <Divider />

        {/* Sub / Super */}
        <Btn onClick={() => editor.chain().focus().toggleSubscript().run()} active={editor.isActive('subscript')} title="Subscript">x₂</Btn>
        <Btn onClick={() => editor.chain().focus().toggleSuperscript().run()} active={editor.isActive('superscript')} title="Superscript">x²</Btn>

        <Divider />

        {/* Colors */}
        <ColorBtn
          color={currentTextColor}
          label="A"
          title="Text color"
          active={false}
          onColorChange={handleTextColor}
        />
        <ColorBtn
          color={currentHighlight}
          label="H"
          isHighlight
          title="Highlight color"
          active={editor.isActive('highlight')}
          onColorChange={handleHighlight}
        />

        <Divider />

        {/* Alignment */}
        <Btn onClick={() => editor.chain().focus().setTextAlign('left').run()} active={editor.isActive({ textAlign: 'left' })} title="Align left">⬤L</Btn>
        <Btn onClick={() => editor.chain().focus().setTextAlign('center').run()} active={editor.isActive({ textAlign: 'center' })} title="Align center">⬤C</Btn>
        <Btn onClick={() => editor.chain().focus().setTextAlign('right').run()} active={editor.isActive({ textAlign: 'right' })} title="Align right">⬤R</Btn>
        <Btn onClick={() => editor.chain().focus().setTextAlign('justify').run()} active={editor.isActive({ textAlign: 'justify' })} title="Justify">⬤J</Btn>

        <Divider />

        {/* Lists */}
        <Btn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} title="Bullet list">• ≡</Btn>
        <Btn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} title="Ordered list">1. ≡</Btn>

        <Divider />

        {/* Block elements */}
        <Btn onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive('blockquote')} title="Blockquote">&ldquo;</Btn>
        <Btn onClick={() => editor.chain().focus().toggleCode().run()} active={editor.isActive('code')} title="Inline code">&lt;/&gt;</Btn>
        <Btn onClick={() => editor.chain().focus().toggleCodeBlock().run()} active={editor.isActive('codeBlock')} title="Code block">{ }</Btn>
        <Btn onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Horizontal rule">—</Btn>
        <Btn onClick={handleLink} active={editor.isActive('link')} title={editor.isActive('link') ? 'Remove link' : 'Add link'}>🔗</Btn>

        <Divider />

        <Btn
          onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}
          title="Clear formatting"
          style={{ fontSize: 11, width: 'auto', padding: '0 6px' }}
        >
          Clear
        </Btn>
      </div>

      <div className={styles.editorContent}>
        <EditorContent editor={editor} />
      </div>
    </div>
  );
};

export default RichTextEditor;
